import { StackShim as Stack, StackItemShim as StackItem } from '@fluentui/react-migration-v8-v9';
import { NeutralColors } from '@fluentui/react';
import { Persona, Spinner, makeStyles } from '@fluentui/react-components';
import React from 'react';
import momentUtc from '../../utils/DateFormatter';
import StatDateFormats from '../../utils/types/DateFormats';
import { IReviewComment } from '../../utils/types/IReview';
import FileSelectorItem from '../common/formFields/fileUpload/fileSelectorItem';

interface IReviewCommentsProps {
  reviewComments: Array<IReviewComment>;
  loading: boolean;
}

const useStyles = makeStyles({
  text: {
    paddingTop: '6px',
  },
  commentText: {
    whiteSpace: 'pre-line',
  },
  personaContainer: {
    paddingTop: '7px',
  },
  personaRow: {
    display: 'flex',
    columnGap: '10px',
  },
  dateText: {
    paddingTop: '10px',
    color: NeutralColors.gray130,
  },
  documentContainer: {
    maxWidth: '500px',
    paddingLeft: '40px',
  },
  timelineItem: {
    height: '15px',
    width: '15px',
    backgroundColor: NeutralColors.gray10,
    marginLeft: '-39px',
    paddingBottom: '5px',
    paddingTop: '5px',
    marginRight: '15px',
  },
  dot: {
    height: '15px',
    width: '15px',
    backgroundColor: NeutralColors.gray60,
    borderRadius: '50%',
    display: 'inline-block',
  },
  listItem: {
    borderLeftColor: NeutralColors.gray60,
    borderLeftWidth: '2px',
    borderLeftStyle: 'solid',
    paddingLeft: '15px',
    paddingBottom: '15px',
  },
  container: {
    padding: '10px 10px 10px 15px',
    backgroundColor: NeutralColors.white,
    borderTopColor: NeutralColors.gray30,
    borderRightColor: NeutralColors.gray30,
    borderBottomColor: NeutralColors.gray30,
    borderLeftColor: NeutralColors.gray30,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
  },
});

const ReviewComments = ({ reviewComments, loading }: IReviewCommentsProps): JSX.Element => {
  const styles = useStyles();

  if (!reviewComments) {
    return <div>No comments have been added yet.</div>;
  }

  if (loading) {
    return <Spinner aria-label="Loading" />;
  }

  return (
    <Stack>
      {reviewComments.map((item, index) => {
        return (
          <div key={item.id} className={styles.listItem}>
            <Stack
              horizontal
              horizontalAlign="space-between"
              tokens={{ childrenGap: 5 }}
              className={styles.container}
            >
              <div className={styles.timelineItem}>
                <span className={styles.dot} />
              </div>
              <StackItem grow={1}>
                <div className={styles.personaRow}>
                  <div className={styles.personaContainer}>
                    <Persona
                      name={item.author.displayName}
                      avatar={{ size: 32, color: 'colorful' }}
                      size="small"
                    />
                  </div>
                  <span className={`${styles.text} ${styles.commentText}`}>{item.text}</span>
                </div>
                {item.document && (
                  <div key={item.document.url} className={styles.documentContainer}>
                    <FileSelectorItem
                      index={index}
                      user={item.document.uploader}
                      url={item.document.url}
                      fileName={item.document.title}
                      uiDisabled={false}
                      allowDelete={false}
                      allowDownload
                      allowViewInTab
                      activity={`Uploaded ${momentUtc(
                        item.document.uploadDate,
                        StatDateFormats.DayBreakout,
                      )}`}
                    />
                  </div>
                )}
              </StackItem>
              <StackItem disableShrink>
                <span className={styles.dateText}>
                  {momentUtc(item.timeStamp, StatDateFormats.DayBreakout)}
                </span>
              </StackItem>
            </Stack>
          </div>
        );
      })}
    </Stack>
  );
};

export default ReviewComments;
